import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { CookieService } from 'ngx-cookie-service';
import { environment } from '../../../../../environments/environment';
import { ApiMethodService } from '../../../../tools/services/api/method-api/apiMethod.service';
import { ActionBackOfficeResponse } from '../../../models/api.model';
import { ServiceCaseTableQuery } from '../../../models/customer-service/customer-service-case.model';

@Injectable({
  providedIn: 'root',
})
export class ApiCustomerServiceCaseService {
  private readonly baseUrl = environment.apiPrivateUrl;
  private readonly cookieService = inject(CookieService);
  private readonly apiMethodService = inject(ApiMethodService);

  // ================= API Functions =================

  /**
   * Retrieves paginated list of service cases with query filter parameters
   */
  getServiceCaseForTable(query: ServiceCaseTableQuery): Observable<any> {
    const token = this.cookieService.get('token');
    const params: Record<string, string | number | boolean> = {
      page: query.page ?? 1,
      pageSize: query.pageSize ?? 20,
    };

    if (query.userId !== undefined && query.userId !== null) params['userId'] = query.userId;
    if (query.siteId !== undefined && query.siteId !== null) params['siteId'] = query.siteId;
    if (query.status) params['status'] = query.status;
    if (query.issueTypeId !== undefined && query.issueTypeId !== null) params['issueTypeId'] = query.issueTypeId;
    if (query.assignedUserId !== undefined && query.assignedUserId !== null) params['assignedUserId'] = query.assignedUserId;
    if (query.startDate) params['startDate'] = query.startDate;
    if (query.endDate) params['endDate'] = query.endDate;
    if (query.searchField) params['searchField'] = query.searchField;
    if (query.searchText) params['searchText'] = query.searchText;

    return this.apiMethodService.request<any>(
      '/CustomerService/getServiceCaseForTable',
      'GET',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'getServiceCaseForTable',
        token,
        headerOnly: false,
        params,
      }
    );
  }

  /**
   * Retrieves detailed service case parameters and chronological logs by ID
   */
  getServiceCaseDetail(id: number): Observable<any> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<any>(
      '/CustomerService/getServiceCaseDetail',
      'GET',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'getServiceCaseDetail',
        token,
        headerOnly: false,
        params: { id },
      }
    );
  }

  /**
   * Submits a request to create a new service case
   */
  createServiceCase(body: any): Observable<ActionBackOfficeResponse> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<ActionBackOfficeResponse>(
      '/CustomerService/createServiceCase',
      'POST',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'createServiceCase',
        token,
        headerOnly: false,
        body,
      }
    );
  }

  /**
   * Submits request to update case status
   */
  updateServiceCaseStatus(body: any): Observable<ActionBackOfficeResponse> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<ActionBackOfficeResponse>(
      '/CustomerService/updateServiceCaseStatus',
      'PATCH',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'updateServiceCaseStatus',
        token,
        headerOnly: false,
        body,
      }
    );
  }

  /**
   * Appends staff replies or internal notes to the case log
   */
  addServiceCaseLog(body: any): Observable<ActionBackOfficeResponse> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<ActionBackOfficeResponse>(
      '/CustomerService/addServiceCaseLog',
      'POST',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'addServiceCaseLog',
        token,
        headerOnly: false,
        body,
      }
    );
  }

  /**
   * Reassigns case to another staff member
   */
  assignServiceCase(body: any): Observable<ActionBackOfficeResponse> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<ActionBackOfficeResponse>(
      '/CustomerService/assignServiceCase',
      'PATCH',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'assignServiceCase',
        token,
        headerOnly: false,
        body,
      }
    );
  }

  /**
   * Cancels service case with reasons list
   */
  cancelServiceCase(body: any): Observable<ActionBackOfficeResponse> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<ActionBackOfficeResponse>(
      '/CustomerService/cancelServiceCase',
      'POST',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'cancelServiceCase',
        token,
        headerOnly: false,
        body,
      }
    );
  }

  /**
   * Uploads one or more proof/attachment images for a service case (cannot delete after save)
   */
  uploadServiceCaseImages(body: any): Observable<ActionBackOfficeResponse> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<ActionBackOfficeResponse>(
      '/CustomerService/uploadServiceCaseImages',
      'POST',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'uploadServiceCaseImages',
        token,
        headerOnly: false,
        body,
      }
    );
  }
}
